import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';

const API_BASE = 'https://collabbay.com/APIControllers';

const Voucher = ({ route, navigation }) => {
  const CollabID = route?.params?.CollabID;

  const [loading, setLoading] = useState(true);
  const [voucher, setVoucher] = useState(null);
  const [postUpload, setPostUpload] = useState(null);

  const [CustomerEmail, setCustomerEmail] = useState('');
  const [AmountPaid, setAmountPaid] = useState('5');

  const [processing, setProcessing] = useState(false);
  const [purchaseResult, setPurchaseResult] = useState(null);

  useEffect(() => {
    fetchVoucherInformation();
  }, [CollabID]);

  const fetchVoucherInformation = async () => {
    if (!CollabID) {
      setLoading(false);
      Alert.alert('Missing voucher', 'No CollabID was provided.');
      return;
    }

    try {
      setLoading(true);

      const response = await fetch(
        `${API_BASE}/FetchVoucherInformation.php?CollabID=${encodeURIComponent(CollabID)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      const data = await response.json();

      if (data.success) {
        setVoucher(data.voucher);
        setPostUpload(data.postUpload || null);
      } else {
        Alert.alert('Error', data.message || 'Could not load voucher.');
      }
    } catch (error) {
      console.error('Fetch voucher error:', error);
      Alert.alert('Error', 'Something went wrong loading this voucher.');
    } finally {
      setLoading(false);
    }
  };

  const handleProxyPurchase = async () => {
    if (!voucher) {
      Alert.alert('Error', 'Voucher not loaded.');
      return;
    }

    if (!CustomerEmail || !AmountPaid) {
      Alert.alert('Missing fields', 'Please enter your email and amount paid.');
      return;
    }

    if (!CustomerEmail.includes('@')) {
      Alert.alert('Invalid email', 'Please enter a valid email address.');
      return;
    }

    if (isNaN(Number(AmountPaid)) || Number(AmountPaid) <= 0) {
      Alert.alert('Invalid amount', 'Amount paid must be greater than 0.');
      return;
    }

    try {
      setProcessing(true);
      setPurchaseResult(null);

      const response = await fetch(`${API_BASE}/ProxyVoucherPurchase.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          CollabID,
          CustomerEmail,
          AmountPaid,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setPurchaseResult(data.purchase);
        Alert.alert('Voucher purchased', 'Proxy purchase complete. Voucher details generated.');
        await fetchVoucherInformation();
      } else {
        Alert.alert('Error', data.message || 'Could not complete purchase.');
      }
    } catch (error) {
      console.error('Proxy purchase error:', error);
      Alert.alert('Error', 'Something went wrong completing this purchase.');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <View style={styles.loadingPage}>
        <ActivityIndicator size="large" color="#000" />
        <Text style={styles.loadingText}>Loading voucher...</Text>
      </View>
    );
  }

  if (!voucher) {
    return (
      <View style={styles.loadingPage}>
        <Ionicons name="alert-circle-outline" size={32} color="#111" />
        <Text style={styles.errorTitle}>Voucher not found</Text>
        <Text style={styles.errorText}>This voucher could not be loaded.</Text>
      </View>
    );
  }

  const stockNumber = Number(voucher.Stock || 0);
  const soldOut = stockNumber <= 0;

  return (
    <ScrollView style={styles.page} contentContainerStyle={styles.pageContent}>
      <View style={styles.topBar}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation?.goBack?.()}
        >
          <Ionicons name="chevron-back" size={20} color="#111" />
          <Text style={styles.backText}>Back</Text>
        </TouchableOpacity>

        <View style={styles.stockPill}>
          <Ionicons name="ticket-outline" size={15} color="#111" />
          <Text style={styles.stockPillText}>
            {soldOut ? 'Sold out' : `${voucher.Stock} left`}
          </Text>
        </View>
      </View>

      <View style={styles.heroCard}>
        <Text style={styles.badgeText}>
          {voucher.Promoter} × {voucher.BrandName}
        </Text>

        <Text style={styles.heroTitle}>
          {voucher.Discount}% off
        </Text>

        <Text style={styles.heroSubtitle}>
          when you spend £{voucher.OnSpend}
        </Text>

        <View style={styles.collabIdPill}>
          <Text style={styles.collabIdText}>CollabID: {voucher.CollabID}</Text>
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>About this voucher</Text>
        <Text style={styles.bodyText}>
          This voucher was assigned through a creator collaboration. Purchase the voucher, then use the redemption link and passcode in-store.
        </Text>

        <View style={styles.infoGrid}>
          <View style={styles.infoBox}>
            <Text style={styles.infoLabel}>Creator</Text>
            <Text style={styles.infoValue}>{voucher.Promoter}</Text>
          </View>

          <View style={styles.infoBox}>
            <Text style={styles.infoLabel}>Venue</Text>
            <Text style={styles.infoValue}>{voucher.BrandName}</Text>
          </View>

          <View style={styles.infoBox}>
            <Text style={styles.infoLabel}>OnSpend</Text>
            <Text style={styles.infoValue}>£{voucher.OnSpend}</Text>
          </View>

          <View style={styles.infoBox}>
            <Text style={styles.infoLabel}>Discount</Text>
            <Text style={styles.infoValue}>{voucher.Discount}%</Text>
          </View>
        </View>
      </View>

      {postUpload ? (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Collaboration content</Text>
          <Text style={styles.bodyText}>
            Content exists for this collaboration in postuploads. We can style this properly once you confirm the exact media column name.
          </Text>
        </View>
      ) : null}

      <View style={styles.purchaseSection}>
        <Text style={styles.purchaseEyebrow}>Proxy payment test</Text>
        <Text style={styles.purchaseTitle}>Buy this voucher</Text>
        <Text style={styles.purchaseText}>
          This currently performs the purchase insert and stock decrement without real Stripe payment. Stripe can be layered on after this flow is proven.
        </Text>

        <View style={styles.inputGroup}>
          <Text style={styles.inputLabel}>CustomerEmail</Text>
          <TextInput
            style={styles.input}
            value={CustomerEmail}
            onChangeText={setCustomerEmail}
            placeholder="customer@email.com"
            placeholderTextColor="#999"
            autoCapitalize="none"
            keyboardType="email-address"
          />
        </View>

        <View style={styles.inputGroup}>
          <Text style={styles.inputLabel}>AmountPaid</Text>
          <TextInput
            style={styles.input}
            value={AmountPaid}
            onChangeText={setAmountPaid}
            placeholder="5"
            placeholderTextColor="#999"
            keyboardType="numeric"
          />
        </View>

        <TouchableOpacity
          style={[
            styles.purchaseButton,
            (processing || soldOut) && styles.purchaseButtonDisabled,
          ]}
          onPress={handleProxyPurchase}
          disabled={processing || soldOut}
        >
          {processing ? (
            <ActivityIndicator color="#fff" />
          ) : (
            <>
              <Ionicons name="card-outline" size={20} color="#fff" />
              <Text style={styles.purchaseButtonText}>
                {soldOut ? 'Sold out' : 'Complete proxy purchase'}
              </Text>
            </>
          )}
        </TouchableOpacity>
      </View>

      {purchaseResult ? (
        <View style={styles.resultCard}>
          <View style={styles.successHeader}>
            <Ionicons name="checkmark-circle" size={26} color="#0a7d28" />
            <Text style={styles.resultTitle}>Voucher generated</Text>
          </View>

          <View style={styles.resultRow}>
            <Text style={styles.resultLabel}>VoucherID</Text>
            <Text style={styles.resultValue}>{purchaseResult.VoucherID}</Text>
          </View>

          <View style={styles.resultRow}>
            <Text style={styles.resultLabel}>VoucherPasscode</Text>
            <Text style={styles.resultValue}>{purchaseResult.VoucherPasscode}</Text>
          </View>

          <View style={styles.resultRow}>
            <Text style={styles.resultLabel}>RemainingStock</Text>
            <Text style={styles.resultValue}>{purchaseResult.RemainingStock}</Text>
          </View>

          <View style={styles.resultLinkBox}>
            <Text style={styles.resultLabel}>RedeemLink</Text>
            <Text selectable style={styles.resultLink}>
              {purchaseResult.RedeemLink}
            </Text>
          </View>
        </View>
      ) : null}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  page: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  pageContent: {
    padding: 18,
    paddingBottom: 60,
    width: '100%',
    maxWidth: 920,
    alignSelf: 'center',
  },
  loadingPage: {
    flex: 1,
    backgroundColor: '#fff',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
    color: '#666',
  },
  errorTitle: {
    marginTop: 10,
    fontSize: 20,
    fontWeight: '900',
    color: '#111',
  },
  errorText: {
    marginTop: 6,
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
  },
  topBar: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 14,
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    borderRadius: 999,
    paddingVertical: 9,
    paddingHorizontal: 12,
    borderWidth: 1,
    borderColor: '#e8e8e8',
  },
  backText: {
    marginLeft: 4,
    fontSize: 14,
    fontWeight: '700',
    color: '#111',
  },
  stockPill: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    borderRadius: 999,
    paddingVertical: 9,
    paddingHorizontal: 12,
    borderWidth: 1,
    borderColor: '#e8e8e8',
  },
  stockPillText: {
    marginLeft: 6,
    fontSize: 13,
    fontWeight: '900',
    color: '#111',
  },
  heroCard: {
    backgroundColor: '#111',
    borderRadius: 30,
    padding: 26,
    alignItems: 'center',
    marginBottom: 18,
  },
  badgeText: {
    color: '#FDF05D',
    fontSize: 14,
    fontWeight: '900',
    textAlign: 'center',
    marginBottom: 16,
  },
  heroTitle: {
    color: '#fff',
    fontSize: 48,
    fontWeight: '900',
    textAlign: 'center',
  },
  heroSubtitle: {
    color: '#e8e8e8',
    fontSize: 22,
    fontWeight: '800',
    textAlign: 'center',
    marginTop: 4,
  },
  collabIdPill: {
    marginTop: 18,
    backgroundColor: '#222',
    borderRadius: 999,
    paddingVertical: 8,
    paddingHorizontal: 13,
  },
  collabIdText: {
    color: '#d8d8d8',
    fontSize: 12,
    fontWeight: '700',
  },
  section: {
    backgroundColor: '#fff',
    borderRadius: 24,
    padding: 18,
    marginBottom: 18,
    borderWidth: 1,
    borderColor: '#e8e8e8',
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: '900',
    color: '#111',
    marginBottom: 8,
  },
  bodyText: {
    fontSize: 14,
    lineHeight: 21,
    color: '#666',
  },
  infoGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    marginTop: 16,
  },
  infoBox: {
    flexGrow: 1,
    flexBasis: '45%',
    backgroundColor: '#f7f7f7',
    borderRadius: 18,
    padding: 14,
    borderWidth: 1,
    borderColor: '#ededed',
  },
  infoLabel: {
    fontSize: 12,
    fontWeight: '900',
    color: '#777',
    marginBottom: 5,
  },
  infoValue: {
    fontSize: 16,
    fontWeight: '900',
    color: '#111',
  },
  purchaseSection: {
    backgroundColor: '#fff',
    borderRadius: 26,
    padding: 20,
    marginBottom: 18,
    borderWidth: 1,
    borderColor: '#e8e8e8',
  },
  purchaseEyebrow: {
    color: '#777',
    fontSize: 12,
    fontWeight: '900',
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 8,
  },
  purchaseTitle: {
    fontSize: 24,
    fontWeight: '900',
    color: '#111',
    marginBottom: 8,
  },
  purchaseText: {
    fontSize: 14,
    color: '#666',
    lineHeight: 21,
    marginBottom: 16,
  },
  inputGroup: {
    marginBottom: 14,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '900',
    color: '#111',
    marginBottom: 7,
  },
  input: {
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#dcdcdc',
    borderRadius: 14,
    paddingHorizontal: 14,
    paddingVertical: 13,
    fontSize: 15,
    color: '#111',
    outlineStyle: 'none',
  },
  purchaseButton: {
    backgroundColor: '#111',
    borderRadius: 999,
    paddingVertical: 15,
    paddingHorizontal: 18,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    marginTop: 4,
  },
  purchaseButtonDisabled: {
    opacity: 0.55,
  },
  purchaseButtonText: {
    color: '#fff',
    fontSize: 15,
    fontWeight: '900',
    marginLeft: 8,
  },
  resultCard: {
    backgroundColor: '#eaf8ee',
    borderColor: '#bfe7ca',
    borderWidth: 1,
    borderRadius: 24,
    padding: 18,
  },
  successHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 14,
  },
  resultTitle: {
    marginLeft: 8,
    fontSize: 20,
    fontWeight: '900',
    color: '#0a7d28',
  },
  resultRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
    paddingVertical: 9,
    borderBottomWidth: 1,
    borderBottomColor: '#cfeedd',
  },
  resultLabel: {
    fontSize: 13,
    fontWeight: '900',
    color: '#276b39',
  },
  resultValue: {
    fontSize: 13,
    fontWeight: '900',
    color: '#111',
    textAlign: 'right',
  },
  resultLinkBox: {
    marginTop: 12,
  },
  resultLink: {
    marginTop: 6,
    fontSize: 13,
    fontWeight: '800',
    color: '#111',
    backgroundColor: '#fff',
    padding: 10,
    borderRadius: 12,
  },
});

export default Voucher;